import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Bookmark, Plus, Trash2, Search, AlertCircle } from 'lucide-react';
import ImageModal from './ImageModal';

interface EditGroupProductsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (products: any[]) => Promise<void>;
  groupName: string;
  initialProducts: any[];
  availableProducts: any[]; // Productos disponibles para agregar
}

const EditGroupProductsModal: React.FC<EditGroupProductsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  groupName,
  initialProducts,
  availableProducts
}) => {
  const { t } = useTranslation();
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [actualSearchTerm, setActualSearchTerm] = useState(''); // Término de búsqueda que se ejecuta
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Estados para el modal de imagen
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{
    url: string;
    title: string;
  } | null>(null);

  // Resetear cuando se abre el modal
  useEffect(() => {
    if (isOpen) {

      setSelectedProducts([...initialProducts]);
      setSearchTerm('');
      setError(null);
    }
  }, [isOpen, initialProducts, availableProducts, groupName]);

  const handleRemoveProduct = (productIndex: number) => {
    const newProducts = selectedProducts.filter((_, index) => index !== productIndex);
    setSelectedProducts(newProducts);
  };

  const handleAddProduct = (product: any) => {
    // Verificar si el producto ya está en el grupo (comparar solo por título y marca)
    const isAlreadySelected = selectedProducts.some(selected => {
      return getProductTitle(selected) === getProductTitle(product) && 
             getProductBrand(selected) === getProductBrand(product);
    });

    if (!isAlreadySelected) {
      setSelectedProducts([...selectedProducts, product]);
    }
  };

  const handleSave = async () => {
    setError(null);
    
    if (selectedProducts.length === 0) {
      setError(t('comparison_groups.no_products_to_save'));
      return;
    }

    setIsSaving(true);

    try {
      await onSave(selectedProducts);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('comparison_groups.error_update'));
    } finally {
      setIsSaving(false);
    }
  };

  // Helper functions para obtener datos del producto
  const getProductTitle = (product: any) => {
    return product.titulo || product.title || product.TITULO || t('comparison_groups.untitled_product');
  };

  const getProductBrand = (product: any) => {
    return product.Marca || product.marca || product.MARCA || '';
  };

  const getProductPrice = (product: any) => {
    // Buscar precio original en los campos disponibles de la base de datos
    // Priorizar 'precio' que corresponde a PRICE en la tabla
    const precio = product.precio || product.original_price || product.PRECIO || 0;
    
    // Si no hay precio base, usar precio de venta como fallback
    if (!precio || precio === 0) {
      return product.precio_venta || product.sale_price || product.PRECIO_VENTA || 0;
    }
    
    return precio;
  };

    const getProductDiscountPrice = (product: any) => {
    // 1. Primero intentar con precioOriginal/precioDesc (formato timeline)
    const precioOriginal = product.precioOriginal && !isNaN(product.precioOriginal) ? product.precioOriginal : null;
    const precioDesc = product.precioDesc && !isNaN(product.precioDesc) ? product.precioDesc : null;
    
    if (precioDesc && precioOriginal && precioDesc < precioOriginal) {
      return precioDesc;
    }
    
    // 2. Usar la misma lógica que el servidor: buscar precio_desc vs precio original
    const originalPrice = getProductPrice(product);
    
    // Buscar precio con descuento en varios campos posibles
    let discountValue = null;
    const discountFields = [
      'precio_desc', 'PRECIO_DESC', 'precio_descuento', 'discounted_price'
    ];
    
    for (const field of discountFields) {
      if (product[field] && product[field] !== "null" && product[field] !== '' && product[field] !== null) {
        discountValue = product[field];
        break;
      }
    }
    
    if (!discountValue) return null;
    
    // Convertir a número si es string
    if (typeof discountValue === 'string') {
      discountValue = discountValue.replace(/[^0-9.-]/g, '');
    }
    
    const discountPrice = parseFloat(discountValue);
    
    // Validar que sea un descuento real (menor al precio original)
    if (isNaN(discountPrice) || discountPrice <= 0 || discountPrice >= originalPrice) {
      return null;
    }
    
    return discountPrice;
  };

  const formatPrice = (price: number) => {
    return price.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const getProductImage = (product: any) => {
    return product.imagen || product.image || product.IMAGEN || product.Imagen || product.IMAGE || '';
  };

  // Funciones para manejar el modal de imagen
  const openImageModal = (imageUrl: string, title: string) => {
    setSelectedImage({
      url: imageUrl,
      title: title
    });
    setImageModalOpen(true);
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage(null);
  };

  // Nueva lógica de búsqueda: buscar en productos seleccionados Y disponibles
  const handleSearch = () => {
    setActualSearchTerm(searchTerm);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getSearchResults = () => {
    if (actualSearchTerm === '') {
      return { allProducts: [] };
    }

    const searchLower = actualSearchTerm.toLowerCase();

    // Buscar TODOS los productos que coincidan con el término de búsqueda
    const allMatchingProducts = availableProducts.filter((product, index) => {
      const productTitle = getProductTitle(product);
      const productBrand = getProductBrand(product);
      
      const matchesSearch = productTitle.toLowerCase().includes(searchLower) ||
                           productBrand.toLowerCase().includes(searchLower);

      return matchesSearch;
    });

    // Eliminar duplicados basándose en título + marca (ya que no hay IDs únicos)
    const uniqueProducts = allMatchingProducts.filter((product, index, self) => {
      const productKey = `${getProductTitle(product)}_${getProductBrand(product)}`;
      return index === self.findIndex(p => 
        `${getProductTitle(p)}_${getProductBrand(p)}` === productKey
      );
    });

    return { allProducts: uniqueProducts };
  };

  const searchResults = getSearchResults();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Bookmark className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              {t('comparison_groups.edit_products')} - {groupName}
            </h3>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {selectedProducts.length}
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Selected Products (Left Panel) */}
          <div className="w-1/2 border-r border-gray-200 flex flex-col">
            <div className="flex-1 overflow-y-auto p-4">
              {selectedProducts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Bookmark className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">{t('comparison_groups.no_products_selected')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {selectedProducts.map((product, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        {/* Imagen del producto */}
                        <div className="flex-shrink-0">
                          {getProductImage(product) ? (
                            <img
                              src={getProductImage(product)}
                              alt={getProductTitle(product)}
                              className="w-10 h-10 object-cover rounded-lg border border-blue-200 cursor-pointer hover:border-blue-400 transition-colors"
                              onClick={() => openImageModal(getProductImage(product), getProductTitle(product))}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const placeholder = target.nextElementSibling as HTMLElement;
                                if (placeholder) placeholder.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          {/* Placeholder para cuando no hay imagen o falla */}
                          <div className={`w-10 h-10 bg-blue-100 rounded-lg border border-blue-200 flex items-center justify-center ${getProductImage(product) ? 'hidden' : ''}`}>
                            <span className="text-blue-400 text-xs">📦</span>
                          </div>
                        </div>
                        
                        {/* Información del producto */}
                        <div className="flex-1 min-w-0">
                          <div className="space-y-1">
                            <span className="text-sm text-gray-900 truncate block">
                              {getProductBrand(product) && `${getProductBrand(product)} - `}
                              {getProductTitle(product)}
                            </span>
                            {getProductPrice(product) > 0 && (
                              <div className="text-left">
                                {(() => {
                                  const originalPrice = getProductPrice(product);
                                  const discountPrice = getProductDiscountPrice(product);
                                  const hasDiscount = discountPrice && discountPrice > 0 && discountPrice < originalPrice;
                                  const finalPrice = hasDiscount ? discountPrice : originalPrice;
                                  

                                  
                                  if (hasDiscount) {
                                    // Producto con descuento - todo en una línea como el timeline
                                    const savings = originalPrice - discountPrice;
                                    const percentage = ((savings / originalPrice) * 100).toFixed(1);
                                    return (
                                      <div className="text-xs font-medium">
                                        <span className="text-gray-500" style={{ textDecoration: 'line-through', textDecorationStyle: 'dotted' }}>
                                          ${formatPrice(originalPrice)}
                                        </span>
                                        <span className="mx-1 text-gray-400">→</span>
                                        <span className="text-blue-600">
                                          ${formatPrice(finalPrice)}
                                        </span>
                                        <span className="text-xs text-green-600 ml-1">
                                          {t('comparison_groups.savings')}: ${formatPrice(savings)} ({percentage}%)
                                        </span>
                                      </div>
                                    );
                                  } else {
                                    // Producto sin descuento
                                    return (
                                      <div className="text-xs font-medium text-blue-600">
                                        ${formatPrice(finalPrice)}
                                      </div>
                                    );
                                  }
                                })()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveProduct(index)}
                        className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                        title={t('comparison_groups.remove_product')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Search Panel (Right Panel) */}
          <div className="w-1/2 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <div className="flex gap-2">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none text-sm"
                  />
                </div>
                <button
                  onClick={handleSearch}
                  className="px-3 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 focus:outline-none transition-colors flex items-center justify-center"
                  title={t('comparison_groups.search_button')}
                >
                  <Search className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {actualSearchTerm === '' ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-xs max-w-48 mx-auto leading-relaxed">
                    {t('comparison_groups.search_instruction')}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Todos los productos que coinciden con la búsqueda */}
                  {searchResults.allProducts.length > 0 ? (
                    <div>
                      <h5 className="text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide">
                        {t('comparison_groups.search_results')} ({searchResults.allProducts.length})
                      </h5>
                      <div className="space-y-2">
                        {searchResults.allProducts.map((product: any, index: number) => {
                          // Verificar si el producto ya está seleccionado
                          const isAlreadySelected = selectedProducts.some(selected => {
                            return getProductTitle(selected) === getProductTitle(product) && 
                                   getProductBrand(selected) === getProductBrand(product);
                          });
                          
                          return (
                            <div 
                              key={`search-${index}`} 
                              className={`flex items-center justify-between p-3 rounded-lg border ${
                                isAlreadySelected 
                                  ? 'bg-blue-50 border-blue-200' 
                                  : 'bg-white border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center space-x-3 flex-1 min-w-0">
                                {/* Imagen del producto */}
                                <div className="flex-shrink-0">
                                  {getProductImage(product) ? (
                                    <img
                                      src={getProductImage(product)}
                                      alt={getProductTitle(product)}
                                      className={`w-10 h-10 object-cover rounded-lg border cursor-pointer transition-colors ${
                                        isAlreadySelected 
                                          ? 'border-blue-200 hover:border-blue-400' 
                                          : 'border-gray-200 hover:border-gray-400'
                                      }`}
                                      onClick={() => openImageModal(getProductImage(product), getProductTitle(product))}
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                        const placeholder = target.nextElementSibling as HTMLElement;
                                        if (placeholder) placeholder.style.display = 'flex';
                                      }}
                                    />
                                  ) : null}
                                  <div className={`w-10 h-10 rounded-lg border flex items-center justify-center ${
                                    isAlreadySelected 
                                      ? 'bg-blue-100 border-blue-200' 
                                      : 'bg-gray-100 border-gray-200'
                                  } ${getProductImage(product) ? 'hidden' : ''}`}>
                                    <span className={`text-xs ${isAlreadySelected ? 'text-blue-400' : 'text-gray-400'}`}>📦</span>
                                  </div>
                                </div>
                                
                                {/* Información del producto */}
                                <div className="flex-1 min-w-0">
                                  <div className="space-y-1">
                                    <span className="text-sm text-gray-900 truncate block">
                                      {getProductBrand(product) && `${getProductBrand(product)} - `}
                                      {getProductTitle(product)}
                                    </span>
                                    {getProductPrice(product) > 0 && (
                                      <div className="text-left">
                                        {(() => {
                                          const originalPrice = getProductPrice(product);
                                          const discountPrice = getProductDiscountPrice(product);
                                          const hasDiscount = discountPrice && discountPrice > 0 && discountPrice < originalPrice;
                                          const finalPrice = hasDiscount ? discountPrice : originalPrice;
                                          
                                          if (hasDiscount) {
                                            const savings = originalPrice - discountPrice;
                                            const percentage = ((savings / originalPrice) * 100).toFixed(1);
                                            return (
                                              <div className="text-xs font-medium">
                                                <span className="text-gray-500" style={{ textDecoration: 'line-through', textDecorationStyle: 'dotted' }}>
                                                  ${formatPrice(originalPrice)}
                                                </span>
                                                <span className="mx-1 text-gray-400">→</span>
                                                <span className={isAlreadySelected ? 'text-blue-600' : 'text-gray-600'}>
                                                  ${formatPrice(finalPrice)}
                                                </span>
                                                <span className="text-xs text-green-600 ml-1">
                                                  {t('comparison_groups.savings')}: ${formatPrice(savings)} ({percentage}%)
                                                </span>
                                              </div>
                                            );
                                          } else {
                                            return (
                                              <div className={`text-xs font-medium ${isAlreadySelected ? 'text-blue-600' : 'text-gray-600'}`}>
                                                ${formatPrice(finalPrice)}
                                              </div>
                                            );
                                          }
                                        })()}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Botón de acción */}
                              {isAlreadySelected ? (
                                <button
                                  onClick={() => {
                                    const productIndex = selectedProducts.findIndex(p => {
                                      return getProductTitle(p) === getProductTitle(product) && 
                                             getProductBrand(p) === getProductBrand(product);
                                    });
                                    if (productIndex !== -1) {
                                      handleRemoveProduct(productIndex);
                                    }
                                  }}
                                  className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                  title={t('comparison_groups.remove_from_group')}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleAddProduct(product)}
                                  className="ml-2 p-1 text-green-500 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
                                  title={t('comparison_groups.add_to_group')}
                                >
                                  <Plus className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    /* Mensaje cuando no hay resultados */
                    <div className="text-center py-8 text-gray-500">
                      <Search className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm">
                        {t('comparison_groups.no_search_results')} "{actualSearchTerm}"
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="p-4 border-t border-gray-200">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-4 w-4 text-red-500" />
                <span className="text-sm text-red-700">{error}</span>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || selectedProducts.length === 0}
            className="flex items-center space-x-2 px-4 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 border border-blue-200 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Bookmark className="h-4 w-4" />
            <span>{isSaving ? t('common.saving') : t('common.save')}</span>
          </button>
        </div>
      </div>
      
      {/* Modal de imagen */}
      <ImageModal
        isOpen={imageModalOpen}
        onClose={closeImageModal}
        imageUrl={selectedImage?.url || ''}
        productTitle={selectedImage?.title || ''}
      />
    </div>
  );
};

export default EditGroupProductsModal; 